import flatMap from 'lodash/flatMap';

import type {Segmentation} from 'common/widgets/property-filter-menu/models/segfilter';
import type {SerializedBehaviorDictionary} from 'common/widgets/cohorts-query-builder/models/types/serialized-cohort-types';
import type {Expression} from 'common/widgets/cohorts-query-builder/models/types/expression-types';
import {PropertyType, ResourceType} from 'common/widgets/property-filter-menu/models/types';
/* eslint-disable-next-line import/no-cycle */
import type {QueryCohortEntry} from 'common/widgets/query-entry-section/types';

export interface SavedCohortSegment {
  [ResourceType.Cohort]: {
    id: number;
    name?: string;
    negated: boolean;
    data_group_id?: string;
  };
}

export interface InlineCohortSegment {
  [ResourceType.Cohort]: {
    name?: string;
    raw_cohort: {
      name: string;
      behaviors: SerializedBehaviorDictionary;
      selector: Expression;
      data_group_id?: string;
    };
    negated: boolean;
  };
}

export function isEmptySegmentSelection(selection: Segment[]) {
  return (
    // selection should never be falsy, but included just as a safety check.
    !selection ||
    // An empty selection can occur for a cohort segment when all cohort items are unselected.
    (Array.isArray(selection) && selection.length === 0)
  );
}

export function isNonEmptySegmentSelection(selection: Segment[]) {
  return Array.isArray(selection) && selection.length !== 0;
}

export function isCohortSegment(segment: Segment): segment is CohortSegment[] {
  return Array.isArray(segment) && !!Object.prototype.hasOwnProperty.call(segment[0] ?? {}, ResourceType.Cohort);
}
export type CohortSegment = SavedCohortSegment | InlineCohortSegment;

export function isCohortSegmentItem(item: QueryCohortEntry | CohortSegment): item is CohortSegment {
  return Object.prototype.hasOwnProperty.call(item, ResourceType.Cohort);
}

export function isInlineCohortSegment(segment: CohortSegment): segment is InlineCohortSegment {
  return !!(segment as InlineCohortSegment).cohort.raw_cohort;
}

export function isUserSegment(segment: Segment): segment is UserSegment {
  return Object.prototype.hasOwnProperty.call(segment, ResourceType.User);
}
export interface UserSegment {
  [ResourceType.User]: Segmentation;
}

export function isEventSegment(segment: Segment): segment is EventSegment {
  return Object.prototype.hasOwnProperty.call(segment, ResourceType.Event);
}
export interface EventSegment {
  [ResourceType.Event]: Segmentation;
}

export function isOtherSegment(segment: Segment): segment is OtherSegment {
  return Object.prototype.hasOwnProperty.call(segment, ResourceType.Other);
}
export interface OtherSegment {
  [ResourceType.Other]: {
    value: string;
  };
}

export function isNumericSegment(segment: Segment) {
  if (isEventSegment(segment)) {
    return segment.event.propertyType === PropertyType.Number;
  }
  if (isUserSegment(segment)) {
    return segment.user.propertyType === PropertyType.Number;
  }
  return false;
}

export function isMultiGroupBy(groupBy: Segment[] | Segment[][]): groupBy is Segment[][] {
  return (
    Array.isArray(groupBy) && !!groupBy.length && Array.isArray(groupBy[0]) && !isCohortSegment(groupBy[0] as Segment)
  );
}

/** A single segment for breaking down a query */
export type Segment = EventSegment | UserSegment | CohortSegment[] | OtherSegment;

/**
 * Infers selection type from the first selected segment
 * TODO: this will change in the future for multi-segment
 */
export function getSegmentType(selection: Segment | Segment[]): ResourceType {
  const hasSelected = Array.isArray(selection) && selection.length && selection[0];

  if (!hasSelected) {
    return null;
  }

  const selectionItem = flatMap(selection)[0];
  return Object.values(ResourceType).find((resourceType) => resourceType in selectionItem) || null;
}
